// frontend/src/hooks/usePOMutations.js
//
// Mutation hooks for the Purchase Orders resource.
//
// Every write that changes a purchase order names the purchase orders as what
// it changed (`queries/dataChanged`): a PO feeds the stock on the way, the
// open orders and the KPIs on most pages, and the PO list is a server-side
// grid with its own row cache. `mutateAsync` resolves only once the queries on
// screen have been read again — the details page shows the new status or the
// booked quantities straight from the server, never a flash of the old
// value. A refused write announces nothing: the server changed nothing.
//
// `useShowDraftPrices` is not a PO write at all: it is the Store settings
// switch for draft prices, offered on a draft's page, and every draft on
// screen reads it.

import { useApiMutation } from '../queries/useApiMutation';
import { apiJson } from '../queries/api';
import { DATA } from '../queries/dataChanged';
import { sessionService } from '../services/SessionApiService';

const API = '/api/po-calculations/purchase-orders/';


// A write that changes a purchase order: the request, then the readers
// re-read, awaited. `edits`: see useApiMutation.
function usePOWrite(mutationFn, changes = [DATA.purchaseOrders], edits = []) {
  return useApiMutation({ mutationFn, changes, edits, awaitChanges: true });
}


/**
 * Move the expected delivery date of an issued PO the supplier still owes
 * on. The PO grid's inline "Expected delivery date" cell edit and the PO details page's
 * delivery date field are the callers. The backend lands the
 * date on the header and on every line, and refuses a PO with nothing owed
 * (``po_not_outstanding``: draft / closed / cancelled / fully received), a
 * past date (``need_by_date_in_past``) and an ERP-owned mirror (409).
 *
 * Variables: { id, needByDate } — an ISO date. An empty value is sent as is
 * and refused by the server (an issued PO always carries a date), so the
 * grid reverts the cell with the translated error rather than guessing.
 */
export function useReschedulePO() {
  return usePOWrite(({ id, needByDate }) =>
    apiJson.post(`${API}${id}/reschedule/`, { need_by_date: needByDate }),
  [DATA.purchaseOrders], [API]);
}


/**
 * Replace a PO's internal note, in any status — the note is the merchant's
 * own and never on the document, so the endpoint has no state gate.
 *
 * Variables: { id, notes }. Over ``PO_NOTES_MAX_LENGTH`` the server answers
 * ``po_notes_too_long``.
 *
 * Awaited like every PO write: the details page keeps the typed text in place
 * until the PO has been re-read — clearing earlier would flash the old note
 * through the field.
 */
export function useUpdatePONotes() {
  return usePOWrite(({ id, notes }) => apiJson.post(`${API}${id}/notes/`, { notes }));
}


/**
 * Flip the store's "show prices on drafts" switch from a draft's page. It is
 * the one Store settings → Purchase orders setting
 * (`purchase_orders.show_prices_on_drafts`): every draft's page, list lines
 * and PDF follow it.
 *
 * Variables: boolean — whether drafts show prices.
 */
export function useShowDraftPrices() {
  return useApiMutation({
    mutationFn: (shown) => sessionService.updateStoreSettings({
      purchase_orders: { show_prices_on_drafts: shown },
    }),
    changes: [DATA.settings],
    awaitChanges: true,
  });
}


/**
 * Transition a draft PO to issued status. The backend records the
 * commitment as `issued_at = now()`, stamps the delivery date, snapshots
 * FX, and recomputes InventoryInTransit.
 *
 * Variables: { id, needByDate } — the ISO date the merchant chose in the
 * issue confirmation, landed on every line; null when they kept the date
 * the backend proposed (GET <id>/issue-date/), so each line keeps its own
 * item's lead time. A past date is refused (``need_by_date_in_past``).
 */
export function useIssuePO() {
  return usePOWrite(({ id, needByDate }) => apiJson.post(
    `${API}${id}/issue/`, needByDate ? { need_by_date: needByDate } : {},
  ));
}


/**
 * Duplicate a PO: the server creates a NEW draft of the same supplier,
 * location, items and quantities, priced as a new order is today, and
 * answers with it. Refused when an item can no longer be ordered from the
 * supplier (400), and on an ERP-owned store, where no
 * Manexys draft exists (400). The caller opens the new PO's tab.
 *
 * Variables: { id }
 */
export function useDuplicatePO() {
  return usePOWrite(({ id }) => apiJson.post(`${API}${id}/duplicate/`));
}


/**
 * Delete a draft. The server refuses an issued PO
 * (``po_issued_cannot_delete``), which is cancelled instead.
 *
 * Variables: id.
 */
export function useDeletePO() {
  return usePOWrite((id) => apiJson.delete(`${API}${id}/`));
}


/**
 * Close a PO as incomplete (short-ship accepted): the merchant keeps what
 * was received and acknowledges the rest won't ship. Only a PO with
 * receipts qualifies — nothing received is Cancel's case, and the backend
 * refuses with ``po_no_receipts_use_cancel`` if the gate is bypassed
 * (surfaced via the apiErrors translation). A Manexys-native PO gets
 * ``closed_at``; an ERP-owned PO is closed short in the ERP and reads as
 * fully received on both sides. Either way incoming is recomputed.
 *
 * Variables: { id, reason? }
 */
export function useClosePO() {
  return usePOWrite(({ id, reason }) => apiJson.post(`${API}${id}/close/`, { reason: reason || '' }));
}


/**
 * Cancel (void) an issued PO that has no receipts yet. Industry rule:
 * once any quantity is received the PO must be closed-incomplete
 * instead, not cancelled. The backend refuses with the structured code
 * ``po_has_receipts_use_close_incomplete`` if the gate is bypassed —
 * surfaced to the merchant via the apiErrors translation.
 *
 * Variables: { id, reason? }
 */
export function useCancelPO() {
  return usePOWrite(({ id, reason }) => apiJson.post(`${API}${id}/cancel/`, { reason: reason || '' }));
}


/**
 * Kick off a PO recommendations recalculation job. Returns immediately
 * with one of:
 *   - { status: 'up_to_date' }   — no recompute needed
 *   - { status: 'started' }      — job queued, caller polls /jobs/?page=1
 *   - { status: 'running' }      — job already running
 *
 * Nothing is announced here: an "up_to_date" response means nothing
 * changed; "started" / "running" mean the merchant will see fresh data only
 * after the job finishes — the polling caller announces the new recommendation
 * run at completion.
 */
export function useRunPORecommendationsJob() {
  return useApiMutation({
    mutationFn: () => apiJson.post('/api/po-calculations/jobs/run/', {}),
    changes: [],
  });
}


/**
 * Submit a receipt for a PO. The endpoint books it on the store's today,
 * applies the qty deltas to inventory, optionally pushes to Shopify, and
 * returns sync status.
 *
 * Variables: { id, lines }
 */
export function useReceiveGoods() {
  return usePOWrite(
    ({ id, lines }) => apiJson.post(`${API}${id}/receive-goods/`, { lines }),
    [DATA.purchaseOrders, DATA.inventory],
  );
}


/**
 * Re-run the Shopify projection of an issued PO — the merchant's retry after
 * a failed projection, or the re-creation of a transfer deleted in Shopify.
 * The response carries the new `channel_projection_status` (+ its issues);
 * the caller reads it rather than assuming success, because a retry that
 * fails again is a 200 with `failed`.
 *
 * Variables: { id }
 */
export function useProjectPO() {
  return usePOWrite(({ id }) => apiJson.post(`${API}${id}/project/`));
}
